import React, { useCallback, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { SkeletonPageBody } from '../components/ui';
import useMemberDashboard from '../hooks/useMemberDashboard';
import useMelioIntelligence from '../hooks/useMelioIntelligence';
import useMyInputsProfile from '../hooks/useMyInputsProfile';
import MemberCareTeamSection from '../components/member/MemberCareTeamSection';
import NextStepHero from '../components/dashboard/home/NextStepHero';
import IntakePrompt from '../components/dashboard/home/IntakePrompt';
import HealthSnapshotStrip from '../components/dashboard/home/HealthSnapshotStrip';
import WhatsChangedSection from '../components/dashboard/home/WhatsChangedSection';
import QuarterStepper from '../components/dashboard/home/QuarterStepper';
import IntelligenceCards from '../components/dashboard/home/IntelligenceCards';
import WatchlistSection from '../components/dashboard/home/WatchlistSection';
import TodayStrip from '../components/dashboard/home/TodayStrip';
import ComingUpList from '../components/dashboard/home/ComingUpList';
import RecentActivityList from '../components/dashboard/home/RecentActivityList';
import RecordLinks from '../components/dashboard/home/RecordLinks';
import { hasContent } from '../utils/dashboard/types';

/**
 * Member Dashboard — what /home renders.
 *
 * Composition only: every block is a presentational component fed by
 * `useMemberDashboard`, which owns all the reading and all the deciding. The
 * order below is the answer to three questions, in order — what do I need to
 * do, what has changed, how am I doing — and is asserted by the page test.
 *
 * `melio-stagger` animates direct children in sequence, so the blocks must stay
 * direct children of the wrapper (index.css supplies delays up to twelve).
 */
export default function MemberDashboard() {
  const { currentUser } = useAuth();
  const dashboard = useMemberDashboard(currentUser?.uid);
  const { profile: myInputsProfile } = useMyInputsProfile(currentUser?.uid);
  const [cancellingBookingId, setCancellingBookingId] = useState<string | null>(null);

  /**
   * Members can cancel their own appointment.
   *
   * This became necessary with auto-confirm: booking is now instant, so a
   * mis-booked slot needs an exit that does not involve emailing the
   * coordinator. Cancelling releases the slot back to the calendar for
   * someone else.
   */
  const handleCancelBooking = useCallback(async (bookingId: string) => {
    if (!window.confirm('Cancel this appointment? The slot will be released and you can book another time.')) return;
    setCancellingBookingId(bookingId);
    try {
      // Imported on use, not at module scope. This page composes presentational
      // blocks around one mocked data hook — a static service import would pull
      // Firebase in at load time and break that isolation (and its test).
      const { cancelClinicalHistoryBooking } = await import('../services/clinicalHistoryService');
      await cancelClinicalHistoryBooking(bookingId);
      // Cheapest correct refresh: the dashboard hook owns a dozen reads and
      // has no partial-invalidate, so re-fetch the page rather than let the
      // cancelled appointment linger on screen.
      window.location.reload();
    } catch (error) {
      console.error('cancel booking failed', error);
      window.alert('Sorry — we could not cancel that. Please message your care team.');
      setCancellingBookingId(null);
    }
  }, []);

  // Intelligence depends on the record the dashboard has already loaded, so the
  // context stays null until those reads settle rather than scoring against an
  // empty profile and producing a differently-ranked list a second later.
  const intelligenceContext = useMemo(() => {
    if (dashboard.tierBLoading) return null;
    return {
      myInputsProfile,
      latestPlan: dashboard.latestPlan,
      openPrompts: dashboard.openPrompts,
      familyTree: dashboard.familyTree,
      labResults: dashboard.labResults,
    };
  }, [
    dashboard.tierBLoading,
    dashboard.latestPlan,
    dashboard.openPrompts,
    dashboard.familyTree,
    dashboard.labResults,
    myInputsProfile,
  ]);

  const { items: intelligenceItems } = useMelioIntelligence(intelligenceContext);

  // Whether there is anything on file yet. A member with no record and a member
  // who is fully caught up both produce `nextStep === null`, and the hero used
  // to greet them identically — see NextStepHero. `failed` deliberately does not
  // count as content, so a denied read can never make a member look brand new.
  const hasRecord = useMemo(() => {
    const s = dashboard.sources;
    return (
      hasContent(s.labs) || hasContent(s.reports) || hasContent(s.plan) || hasContent(s.intake)
    );
  }, [dashboard.sources]);

  if (!currentUser) return null;

  // Skeletons rather than a full-viewport spinner: the page keeps its shape
  // instead of blanking the whole content region on every navigation.
  if (dashboard.phase === 'loading') {
    return (
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-10 space-y-8">
        <SkeletonPageBody cards={4} />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-10 space-y-8 melio-stagger">
      {/* The practice comes first. */}
      <MemberCareTeamSection members={dashboard.careTeam} />

      {/* 1 — The history, if it is still unfinished.
          Above the hero, not below it. The hero shows the single most urgent
          thing and the intake is almost never that — it ranks seventh of eight
          — but it GATES the report that every other block on this page is
          building towards, so a member whose history is unfinished should meet
          that fact before they meet their results. It renders nothing once the
          history is ready, at which point the hero is back at the top. */}
      <IntakePrompt progress={dashboard.intakeProgress} />

      {/* 2 — What do I need to do? */}
      <NextStepHero
        action={dashboard.nextStep}
        firstName={dashboard.firstName}
        hasRecord={hasRecord}
      />

      {/* 2 — How am I doing? */}
      <HealthSnapshotStrip chips={dashboard.snapshot} />

      {/* 3 — What has changed? */}
      <WhatsChangedSection
        anchor={dashboard.reviewAnchor}
        biomarkers={dashboard.biomarkerDeltas}
        wearables={dashboard.wearableDeltas}
        status={dashboard.sources.labs}
        baseline={dashboard.resultsBaseline}
      />

      {/* 4 — Where am I in the cycle? */}
      <QuarterStepper progress={dashboard.quarter} />

      {/* 5 — Reading, chosen for this member. */}
      <IntelligenceCards items={intelligenceItems} />

      {/* 6 — What the practice is watching. */}
      <WatchlistSection items={dashboard.watchlist} planReleasedAt={dashboard.planReleasedAt} />

      {/* 7 — Today, in ten seconds. */}
      <TodayStrip today={dashboard.today} />

      {/* 8 — What happens next. */}
      <ComingUpList
        events={dashboard.comingUp}
        onCancelBooking={handleCancelBooking}
        cancellingId={cancellingBookingId}
      />

      {/* 9 — Reassurance, last. */}
      <RecentActivityList entries={dashboard.activity} />

      {/* Navigation into the sub-surfaces the rail doesn't carry. Never hides:
          a member with no data has every content block above collapsed, and
          this is their only route to Intelligence and Reports. */}
      <RecordLinks />
    </div>
  );
}
